import type { WeaponPartFinish, WeaponPartRole, WeaponSkin } from '../../../engine/content/schema/weapon';
import { SKIN_PRESETS } from './skinPresets';

// Mirrors sstk-sunder-web's src/game/crosshair.ts share-code shape: a
// versioned, dash-prefixed magic tag over a compact payload, decoding always
// clamps every field and tolerates a malformed/missing payload rather than
// ever producing an invalid skin. A skin's `parts` is a variable-size map
// (unlike CrosshairConfig's fixed scalar fields), so the payload itself is
// base64url JSON rather than positional dash-delimited tokens - still one
// versioned magic-prefixed string, still copy-paste safe.

const MAGIC = 'GSX1';

export const WEAPON_PART_ROLES: readonly WeaponPartRole[] = [
  'body', 'frame', 'metal', 'grip', 'furniture', 'detail', 'sight', 'optic', 'energy',
];

const ROUGHNESS_METALNESS_RANGE = { min: 0, max: 1 };
const COLOR_RANGE = { min: 0, max: 0xffffff };
const EMISSIVE_INTENSITY_RANGE = { min: 0, max: 4 };

const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n));

function normalizeFinish(input: unknown): WeaponPartFinish | null {
  if (!input || typeof input !== 'object') return null;
  const i = input as Partial<Record<keyof WeaponPartFinish, unknown>>;
  const finish: WeaponPartFinish = {};
  if (typeof i.color === 'number' && Number.isFinite(i.color)) {
    finish.color = clamp(Math.round(i.color), COLOR_RANGE.min, COLOR_RANGE.max);
  }
  if (typeof i.roughness === 'number' && Number.isFinite(i.roughness)) {
    finish.roughness = clamp(i.roughness, ROUGHNESS_METALNESS_RANGE.min, ROUGHNESS_METALNESS_RANGE.max);
  }
  if (typeof i.metalness === 'number' && Number.isFinite(i.metalness)) {
    finish.metalness = clamp(i.metalness, ROUGHNESS_METALNESS_RANGE.min, ROUGHNESS_METALNESS_RANGE.max);
  }
  if (typeof i.emissive === 'number' && Number.isFinite(i.emissive)) {
    finish.emissive = clamp(Math.round(i.emissive), COLOR_RANGE.min, COLOR_RANGE.max);
  }
  if (typeof i.emissiveIntensity === 'number' && Number.isFinite(i.emissiveIntensity)) {
    finish.emissiveIntensity = clamp(i.emissiveIntensity, EMISSIVE_INTENSITY_RANGE.min, EMISSIVE_INTENSITY_RANGE.max);
  }
  return Object.keys(finish).length > 0 ? finish : null;
}

/** Coerce arbitrary stored / decoded input into a valid skin - every role clamped, unknown roles dropped. */
export function normalizeSkin(input: unknown, fallbackId: string, fallbackName: string): WeaponSkin {
  if (!input || typeof input !== 'object') return { id: fallbackId, name: fallbackName, parts: {} };
  const i = input as { id?: unknown; name?: unknown; parts?: unknown };
  const parts: WeaponSkin['parts'] = {};
  if (i.parts && typeof i.parts === 'object') {
    for (const role of WEAPON_PART_ROLES) {
      const finish = normalizeFinish((i.parts as Record<string, unknown>)[role]);
      if (finish) parts[role] = finish;
    }
  }
  return {
    id: typeof i.id === 'string' && i.id.trim() ? i.id : fallbackId,
    name: typeof i.name === 'string' && i.name.trim() ? i.name : fallbackName,
    parts,
  };
}

function toBase64Url(json: string): string {
  const base64 = btoa(unescape(encodeURIComponent(json)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): string | null {
  try {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return decodeURIComponent(escape(atob(padded)));
  } catch {
    return null;
  }
}

export function encodeSkinShareCode(skin: WeaponSkin): string {
  const normalized = normalizeSkin(skin, skin.id, skin.name);
  return `${MAGIC}-${toBase64Url(JSON.stringify({ n: normalized.name, p: normalized.parts }))}`;
}

export function decodeSkinShareCode(code: string, fallbackId: string): WeaponSkin | null {
  if (typeof code !== 'string') return null;
  const trimmed = code.trim();
  const dashIndex = trimmed.indexOf('-');
  if (dashIndex === -1 || trimmed.slice(0, dashIndex).toUpperCase() !== MAGIC) return null;
  const payload = fromBase64Url(trimmed.slice(dashIndex + 1));
  if (!payload) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const { n, p } = parsed as { n?: unknown; p?: unknown };
  return normalizeSkin({ id: fallbackId, name: n, parts: p }, fallbackId, 'Imported skin');
}

/** The id of the preset that exactly matches `skin`'s parts, or null for a custom one. */
export function matchingSkinPreset(skin: WeaponSkin): string | null {
  const partsJson = JSON.stringify(normalizeSkin(skin, skin.id, skin.name).parts);
  return SKIN_PRESETS.find((p) => JSON.stringify(normalizeSkin(p, p.id, p.name).parts) === partsJson)?.id ?? null;
}
